/**
 * @file time-picker/subcomponents/time-field.tsx
 *
 * TimeField — display/input cell for hour or minute.
 *
 * Used in both modes:
 *   - "selector" → <button> in TimePicker (dial), click to switch hour/minute selection
 *   - "input"    → <input> in TimeInput, manual number entry
 *
 * Ported from `TimeSelector` and `TimePickerTextField` composables in TimePicker.kt.
 *
 * @see docs/m3/date-and-time-pickers/time-pickers/TimePicker.kt (TimeSelector, TimePickerTextField)
 */

import * as React from "react";
import { cn } from "../../../../lib/utils";
import { TP_SIZE } from "../time-picker.tokens";
import type { TimePickerSelectionMode } from "../time-picker.types";

// ─── Props ─────────────────────────────────────────────────────────────────────

interface TimeFieldSelectorProps {
	/** Mode "selector" = button in dial picker */
	mode: "selector";
	/** Display value (number, zero-padded) */

	value: number;
	/** Field type: hour or minute */
	field: TimePickerSelectionMode;
	/** Current picker selection mode */
	selection: TimePickerSelectionMode;
	/** Callback when user clicks to activate field */
	onActivate: () => void;
	className?: string;
}

interface TimeFieldInputProps {
	/** Mode "input" = text input in TimeInput */
	mode: "input";
	/** Raw input value (bound to input element) */
	value: number;
	/** Field type: hour or minute */
	field: TimePickerSelectionMode;
	/** Whether field is currently focused (controlled) */
	isFocused: boolean;
	/** Whether value is valid (for error state styling) */
	isValid: boolean;
	/** Callback when value changes */
	onChange: (value: number) => void;
	/** Callback fired when 2 digits are entered → advances focus to next field */
	onAutoAdvance?: () => void;
	/** Callback when field receives focus */
	onFocus?: () => void;
	/** Callback when field loses focus */
	onBlur?: () => void;
	className?: string;
}

type TimeFieldProps = TimeFieldSelectorProps | TimeFieldInputProps;

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * TimeField component — renders hour/minute box according to mode.
 *
 * Selector mode: button with DisplayLarge typography, highlighted when selected.
 * Input mode: text input field with DisplayMedium typography, focus outline when focused.
 *
 * @example Selector mode (in TimePicker)

 * ```tsx
 * <TimeField
 *   mode="selector"
 *   value={state.hourForDisplay}
 *   field="hour"
 *   selection={state.selection}
 *   onActivate={() => state.setSelection('hour')}
 * />
 * ```
 *
 * @example Input mode (trong TimeInput)
 * ```tsx
 * <TimeField
 *   mode="input"
 *   value={state.hourInput}
 *   field="hour"
 *   isFocused={focusedField === 'hour'}
 *   isValid={state.isHourInputValid}
 *   onChange={(v) => state.setHourInput(v)}
 *   onAutoAdvance={() => setFocusedField('minute')}
 * />
 * ```
 */
export const TimeField: React.FC<TimeFieldProps> = (props) => {
	if (props.mode === "selector") {
		return <SelectorField {...props} />;
	}
	return <InputField {...props} />;
};

TimeField.displayName = "TimeField";

// ─── Selector Field (Dial mode) ────────────────────────────────────────────────

const SelectorField: React.FC<TimeFieldSelectorProps> = ({
	value,
	field,
	selection,
	onActivate,
	className,
}) => {
	const isSelected = selection === field;

	return (
		<button
			type="button"
			aria-label={field === "hour" ? "Hour" : "Minute"}
			aria-pressed={isSelected}
			onClick={onActivate}
			style={{
				width: TP_SIZE.timeSelectorWidth,
				height: TP_SIZE.timeSelectorHeight,
			}}
			className={cn(
				// Layout
				"flex items-center justify-center",
				"rounded-m3-sm",
				// Typography — DisplayLarge equivalent (57px)
				"text-[57px] font-normal leading-none tabular-nums",
				// State
				isSelected
					? "bg-m3-primary-container text-m3-on-primary-container"
					: "bg-m3-surface-container-highest text-m3-on-surface",
				// Interaction
				"cursor-pointer transition-colors transition-m3-fast-effects",
				"hover:opacity-90 active:opacity-80",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-m3-primary",
				className,
			)}
		>
			{String(value).padStart(2, "0")}
		</button>
	);
};

// ─── Input Field (Text input mode) ────────────────────────────────────────────

const InputField: React.FC<TimeFieldInputProps> = ({
	value,
	field,
	isFocused,
	isValid,
	onChange,
	onAutoAdvance,
	onFocus,
	onBlur,
	className,
}) => {
	// Track current input content for digit-by-digit handling
	const [inputText, setInputText] = React.useState(
		String(value).padStart(2, "0"),
	);
	const inputRef = React.useRef<HTMLInputElement>(null);

	// Sync from state when value changes externally (e.g. on reset)
	React.useEffect(() => {
		if (!isFocused) {
			setInputText(String(value).padStart(2, "0"));
		}
	}, [value, isFocused]);

	// Focus/blur the input element when isFocused prop changes
	React.useEffect(() => {
		if (isFocused) {
			inputRef.current?.focus();
			inputRef.current?.select();
		}
	}, [isFocused]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const raw = e.target.value.replace(/\D/g, ""); // retain digits only
		const limited = raw.slice(-2); // max 2 digits

		setInputText(limited);

		const numVal = parseInt(limited, 10);
		if (!Number.isNaN(numVal)) {
			onChange(numVal);

			// Auto-advance after entering 2 digits
			if (limited.length === 2) {
				onAutoAdvance?.();
			}
		} else if (limited === "") {
			// Empty input — reset to 0 while keeping display empty
			onChange(0);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
		// Allow: digits, Backspace, Delete, Tab, Arrow keys

		const allowed = [
			"Backspace",
			"Delete",
			"Tab",
			"ArrowLeft",
			"ArrowRight",
			"Enter",
		];
		const isDigit = e.key >= "0" && e.key <= "9";
		if (!isDigit && !allowed.includes(e.key)) {
			e.preventDefault();
		}
	};

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
		e.target.select(); // select all text khi focus
		onFocus?.();
	};

	const displayText = isFocused ? inputText : String(value).padStart(2, "0");

	const isError = isFocused && !isValid && inputText.length === 2;

	return (
		<div
			style={{
				width: TP_SIZE.timeFieldWidth,
				height: TP_SIZE.timeFieldHeight,
			}}
			className={cn(
				"relative flex items-center justify-center rounded-m3-sm",
				// Background state
				isFocused
					? "bg-m3-primary-container"
					: "bg-m3-surface-container-highest",
				// Error border
				isError && "ring-2 ring-m3-error",
				// Focus outline (non-error)
				isFocused && !isError && "ring-2 ring-m3-primary",
				className,
			)}
		>
			<input
				ref={inputRef}
				type="text"
				inputMode="numeric"
				value={displayText}
				maxLength={2}
				aria-label={field === "hour" ? "Hour" : "Minute"}
				aria-invalid={isError}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				onBlur={onBlur}
				className={cn(
					// Reset input styles
					"w-full h-full bg-transparent border-none outline-none",
					"text-center tabular-nums select-all",
					// Typography — DisplayMedium equivalent (45px)
					"text-[45px] font-normal leading-none",
					// Color
					isFocused ? "text-m3-on-primary-container" : "text-m3-on-surface",
					// Caret
					"caret-m3-primary",
				)}
			/>
		</div>
	);
};
